import * as stagesCase from "../../Stages/StagesCase";

import { ProductUnion } from "../../../types/ProductUnion";

export const isLawsuitRevocationWithoutCustomerApproval = (abstractLitigationCase: ProductUnion) =>
  abstractLitigationCase.caseCategory === "defendClaim" &&
  ["lawsuitRevocation", "lawsuitRevocationBeforeHearing"].includes(
    abstractLitigationCase.lawsuitCompletedReason
  );

export const abstractLitigationCaseRequiredFields = {
  [stagesCase.STAGE_CREATED]: () => [],
  [stagesCase.STAGE_DATA_COMPLETED]: () => [],
  [stagesCase.STAGE_WAITING_FOR_FEEDBACK]: (abstractLitigationCase: ProductUnion) =>
    abstractLitigationCaseRequiredFields[stagesCase.STAGE_DATA_COMPLETED_LAWFIRM](abstractLitigationCase),
  [stagesCase.STAGE_DATA_COMPLETED_LAWFIRM]: (abstractLitigationCase: ProductUnion) => {
    let requiredFields = [];

    if (abstractLitigationCase.caseCategoryAssessment === "reject") {
      requiredFields.push("assessmentRejectionText");
      return requiredFields;
    }

    if (["paymentOrder", "enforcementOrder", "lawsuit"].includes(abstractLitigationCase.defenseProcessStatus)) {
      if (abstractLitigationCase.caseCategory === "enforceClaim" && !abstractLitigationCase.isHigherInstanceCase) {
        requiredFields.push("courtPostalCode");
      }
      if (abstractLitigationCase.caseCategory === "defendClaim") {
        requiredFields.push("responsibleCourt");
        requiredFields.push("valueInDisputeJudicialPreliminary");
      }
      if (abstractLitigationCase.defenseProcessStatus === "paymentOrder") {
        requiredFields.push("paymentOrderDate");
      }
      if (abstractLitigationCase.defenseProcessStatus === "enforcementOrder") {
        requiredFields.push("enforcementOrderDate");
      }
      if (abstractLitigationCase.defenseProcessStatus === "lawsuit") {
        requiredFields.push("statementOfClaimFiledDate");
      }
      return requiredFields;
    }
    requiredFields.push("caseCategoryAssessment");
    if (abstractLitigationCase.caseCategoryAssessment !== "reject") {
      requiredFields.push("valueInDisputeExtrajudicialPreliminary");
    }
    if (
      abstractLitigationCase.caseCategory === "enforceClaim" &&
      abstractLitigationCase.caseCategoryAssessment !== "reject"
    ) {
      requiredFields.push("extrajudicialCommunicationDeadline");
    }

    if (abstractLitigationCase.clientWasMinorAtContractDate) {
      requiredFields.push("clientLegalRepresentative.residenceAddress.streetAddress");
      requiredFields.push("clientLegalRepresentative.residenceAddress.postalCode");
      requiredFields.push("clientLegalRepresentative.residenceAddress.addressLocality");
      requiredFields.push("client.birthDate");
    }

    return requiredFields;
  },
  [stagesCase.STAGE_CHECKED]: (abstractLitigationCase: ProductUnion) => {
    const requiredFields =
      abstractLitigationCaseRequiredFields[stagesCase.STAGE_DATA_COMPLETED_LAWFIRM](abstractLitigationCase);
    if (
      abstractLitigationCase.caseCategoryAssessment !== "reject" &&
      !["paymentOrder", "enforcementOrder", "lawsuit"].includes(abstractLitigationCase.defenseProcessStatus)
    ) {
      requiredFields.push("automaticallyGenerateExtrajudicialLetter");
    }
    return requiredFields;
  },
  [stagesCase.STAGE_IN_PROGRESS]: () => [],
  [stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT]: (abstractLitigationCase: ProductUnion) => {
    const requiredFields = ["extrajudicialCommunicationSent"];
    if (abstractLitigationCase.caseCategory === "enforceClaim") {
      requiredFields.push("extrajudicialCommunicationDeadline");
    }
    return requiredFields;
  },
  [stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED]: (abstractLitigationCase: ProductUnion) => {
    let requiredFields = ["extrajudicialReaction"];
    if (abstractLitigationCase.extrajudicialReaction !== "noReaction") {
      requiredFields.push("extrajudicialReactionReceived");
    }

    if (
      ["defendClaim", "enforceClaim"].includes(abstractLitigationCase.caseCategory ?? "") &&
      abstractLitigationCase.extrajudicialReaction !== "fullyAgreed"
    ) {
      requiredFields.push("extrajudicialRecommendation");
    }
    return requiredFields;
  },
  [stagesCase.STAGE_ACTION_REQUIRED]: () => ["finalProcessFeedbackClient"],
  [stagesCase.STAGE_PREPARE_LAWSUIT]: (abstractLitigationCase: ProductUnion) => {
    let requiredFields = [
      "costBearingByOpponentExtrajudicial",
      "valueInDisputeJudicialPreliminary",
      "subtractExtrajudicialProcessFee",
    ];

    if (abstractLitigationCase.extrajudicialCommunicationSent) {
      requiredFields.push("valueInDisputeExtrajudicialFinal");
      if (abstractLitigationCase.paymentType === "insurance") {
        requiredFields.push("caseInsurance.extrajudicialCoverageRequestAnsweredDate");
        requiredFields.push("caseInsurance.extrajudicialCoverageGranted");
      }
    }

    return requiredFields;
  },
  [stagesCase.STAGE_LAWSUIT]: (abstractLitigationCase: ProductUnion) => {
    let requiredFields = [];
    if (abstractLitigationCase.paymentType === "insurance") {
      requiredFields.push("caseInsurance.judicialCoverageRequestAnsweredDate");
      requiredFields.push("caseInsurance.judicialCoverageGranted");
    }
    if (abstractLitigationCase.caseCategory === "enforceClaim") {
      if (!abstractLitigationCase.isHigherInstanceCase) {
        requiredFields.push("courtPostalCode");
      }
      requiredFields.push("statementOfClaimSentDate");
    }
    if (abstractLitigationCase.caseCategory === "defendClaim") {
      requiredFields.push("statementOfClaimFiledDate");
      requiredFields.push("responsibleCourt");
    }

    return requiredFields;
  },
  [stagesCase.STAGE_TRIAL]: () => [],
  [stagesCase.STAGE_EVALUATE_JUDGEMENT]: () => [],
  [stagesCase.STAGE_LAWSUIT_COMPLETED]: (abstractLitigationCase: ProductUnion) => {
    let requiredFields = [
      "lawsuitCompletedReason",
      "lawsuitCompletedDate",
      "valueInDisputeJudicialFinal",
      "hasStandardInvoicing",
    ];
    if (
      !["courtDecision", "defaultJudgement", "judgementByConfession", "courtOrder"].includes(
        abstractLitigationCase.lawsuitCompletedReason
      ) &&
      !isLawsuitRevocationWithoutCustomerApproval(abstractLitigationCase)
    ) {
      requiredFields.push("customerLawsuitCompletedApprovalDate");
    }

    if (
      !["lawsuitRevocation", "lawsuitRevocationBeforeHearing", "courtOrder"].includes(abstractLitigationCase.lawsuitCompletedReason)
    ) {
      requiredFields.push("costBearingByOpponentRelative");
    }

    if (
      abstractLitigationCase.valueInDisputeJudicialFinal &&
      abstractLitigationCase.valueInDisputeExtrajudicialFinal &&
      abstractLitigationCase.valueInDisputeJudicialFinal !== abstractLitigationCase.valueInDisputeExtrajudicialFinal
    ) {
      requiredFields.push("hasExtrajudicialValueInDisputeOverwrite");
    }

    return requiredFields;
  },
  [stagesCase.STAGE_COMPLETED]: (abstractLitigationCase: ProductUnion) => {
    let requiredFields: string[] = [];

    // Currently, Advocard is the only insurance that has monitoring billing
    if (
      abstractLitigationCase.stage >= stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT &&
      abstractLitigationCase.paymentType === "insurance" &&
      abstractLitigationCase.caseCategory !== "advisory" &&
      [2].includes(abstractLitigationCase.caseInsurance.insurer?.id ?? -1) &&
      abstractLitigationCase.caseInsurance.iIWPI
    ) {
      requiredFields.push("hasMonitoringBilling");
    }

    if (abstractLitigationCase.caseWasInLitigation) {
      if (
        !(
          ["lawsuitRevocation", "lawsuitRevocationBeforeHearing"].includes(
            abstractLitigationCase.lawsuitCompletedReason
          ) ||
          abstractLitigationCase.costBearingByOpponentRelative === 0.0
        )
      ) {
        requiredFields.push("costBearingByOpponentFinalAbsolute");
      }
      requiredFields.push("netRvgAmount");
    }

    if (abstractLitigationCase.extrajudicialCommunicationSent) {
      requiredFields.push("valueInDisputeExtrajudicialFinal");
      requiredFields.push("costBearingByOpponentExtrajudicial");
    }

    return requiredFields;
  },
};
